import { ArtistData } from '@/lib/artistData';
import { useTranslations } from '../hooks/useTranslations';

interface ArtistProfileProps {
  artist: ArtistData;
}

const ArtistProfile = ({ artist }: ArtistProfileProps) => {
  const t = useTranslations();

  return (
    <div className="bg-white rounded-3xl border border-border-light shadow-sm p-8 md:p-12 mb-12">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">

        {/* Image / Logo */}
        <div className="flex justify-center md:justify-start">
          {artist.logoImage ? (
            <img
              src={artist.logoImage}
              alt={artist.name}
              className="max-w-sm w-full h-auto rounded-2xl"
            />
          ) : (
            <div className="w-64 h-64 rounded-2xl bg-gradient-to-br from-violet-500/20 to-indigo-500/20 flex items-center justify-center">
              <span className="text-4xl font-bold text-text-primary">{artist.displayName}</span>
            </div>
          )}
        </div>

        {/* Bio + links */}
        <div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-text-primary mb-2 tracking-tight">{artist.name}</h2>
          <p className="text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-5">AI-musikartist</p>

          <p className="text-[15px] text-text-secondary leading-relaxed mb-3">{artist.bio}</p>
          {artist.storyline && (
            <p className="text-[14px] text-text-secondary leading-relaxed mb-6 opacity-80">{artist.storyline}</p>
          )}

          {/* Streaming links */}
          <div className="flex flex-wrap gap-3">
            {artist.spotifyUrl && (
              <a href={artist.spotifyUrl} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#1DB954] hover:bg-[#1ed760] text-white text-sm font-semibold px-4 py-2 rounded-xl transition-all hover:-translate-y-0.5">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/>
                </svg>
                {t.artistsPage?.platforms?.spotify || 'Spotify'}
              </a>
            )}
            {artist.appleMusicUrl && (
              <a href={artist.appleMusicUrl} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#FA243C] hover:bg-[#ff3456] text-white text-sm font-semibold px-4 py-2 rounded-xl transition-all hover:-translate-y-0.5">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                {t.artistsPage?.platforms?.appleMusic || 'Apple Music'}
              </a>
            )}
            {artist.soundcloudUrl && (
              <a href={artist.soundcloudUrl} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#FF5500] hover:bg-[#ff6622] text-white text-sm font-semibold px-4 py-2 rounded-xl transition-all hover:-translate-y-0.5">
                <img src="/soundcloud-logo-icon.svg" alt="SoundCloud" className="w-4 h-4 filter brightness-0 invert" />
                {t.artistsPage?.platforms?.soundcloud || 'SoundCloud'}
              </a>
            )}
            {artist.tiktokUrl && (
              <a href={artist.tiktokUrl} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-black hover:bg-gray-800 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-all hover:-translate-y-0.5">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
                {t.artistsPage?.platforms?.tiktok || 'TikTok'}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtistProfile;
